/**
 * Utility for generating application URLs.
 * Dynamically resolves to the current environment's origin (localhost, staging, production).
 * No hardcoded URLs — works identically in dev and prod.
 */
export const getAppUrl = (): string => {
    if (typeof window !== 'undefined') {
        return window.location.origin;
    }
    // SSR / Edge fallback — should never hit in a Vite SPA
    return 'https://app.forgedash.uk';
};

/**
 * Points a storage URL minted by an edge function at the public API origin.
 *
 * Edge functions build their client from SUPABASE_URL, which on the
 * self-hosted stack is the INTERNAL gateway (`http://supabase-kong:8000`), so
 * every signed URL they return names a host no browser can resolve. The
 * signature covers the object path only, never the host, so re-basing the
 * path onto VITE_SUPABASE_URL keeps it valid.
 *
 * Only `/storage/v1/` paths are re-based; anything else comes back unchanged.
 */
export const toPublicStorageUrl = (
    url: string,
    publicBase: string | undefined = import.meta.env.VITE_SUPABASE_URL,
): string => {
    if (!publicBase) return url;
    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return url;
    }
    if (!parsed.pathname.startsWith('/storage/v1/')) return url;
    return publicBase.replace(/\/+$/, '') + parsed.pathname + parsed.search;
};
